export type SidebarActiveNavItem = {
  name: string
  activeRoutes?: string[]
  hideInSidebar?: boolean
  children?: SidebarActiveNavItem[]
}

export type SidebarCurrentRoute = {
  name?: string | symbol | null
  path: string
  matchedNames: Array<string | symbol | null | undefined>
}

export function normalizeSidebarPath(path: string): string {
  if (!path || path === '/') {
    return '/'
  }

  return path.replace(/\/+$/, '') || '/'
}

function currentRouteName(current: SidebarCurrentRoute): string {
  return typeof current.name === 'string' ? current.name : ''
}

/**
 * True when this sidebar item is the page the user is on: own name, related
 * `activeRoutes`, a matched record, or the same path as the item's destination
 * (parent `/finance` vs child `finance-index`).
 */
export function isSidebarNavItemActive(
  item: SidebarActiveNavItem,
  current: SidebarCurrentRoute,
  resolvedItemPath?: string | null,
): boolean {
  const name = currentRouteName(current)

  if (name && (name === item.name || item.activeRoutes?.includes(name))) {
    return true
  }

  if (current.matchedNames.some((matched) => matched === item.name)) {
    return true
  }

  if (!resolvedItemPath) {
    return false
  }

  return normalizeSidebarPath(current.path) === normalizeSidebarPath(resolvedItemPath)
}

export function isSidebarSectionActive(
  section: SidebarActiveNavItem,
  current: SidebarCurrentRoute,
  resolvedPathFor: (routeName: string) => string | null,
): boolean {
  if (!section.children?.length) {
    return isSidebarNavItemActive(section, current, resolvedPathFor(section.name))
  }

  if (
    section.children.some(
      (child) => child.hideInSidebar || isSidebarNavItemActive(child, current, resolvedPathFor(child.name)),
    )
  ) {
    return true
  }

  const name = currentRouteName(current)
  if (name && (name === section.name || section.activeRoutes?.includes(name))) {
    return true
  }

  return current.matchedNames.some((matched) => matched === section.name)
}
